import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Checkbox, Chip, Button, Stack, Divider
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ReportProblem as UrgentIcon,
  Warning as ImportantIcon,
  Visibility as WatchIcon,
  AddTask as AddTaskIcon,
  Inventory2Outlined as ExistingIcon
} from '@mui/icons-material';
import { localized, UI } from '../../data/questionnaireContent';

const BUCKETS = [
  { key: 'urgent', color: '#E74C3C', icon: <UrgentIcon />, uiKey: 'bucketUrgent' },
  { key: 'important', color: '#E67E22', icon: <ImportantIcon />, uiKey: 'bucketImportant' },
  { key: 'surveiller', color: '#F1C40F', icon: <WatchIcon />, uiKey: 'bucketWatch' }
];

function existingLabel(existing, lang) {
  if (existing.validation_status === 'rejete' && existing.validated_at) {
    const date = new Date(existing.validated_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
    return localized(UI.alreadyExistsRejected, lang).replace('{date}', date);
  }
  return localized(UI.alreadyExists, lang);
}

function AssessmentResults({ proposals, onAccept, creating, lang }) {
  const { t } = useTranslation();
  const newProposals = proposals.filter((p) => !p.existing);
  const existingProposals = proposals.filter((p) => p.existing);
  // Par défaut tout est coché (l'utilisatrice décoche ce qu'elle ne veut pas)
  const [selected, setSelected] = useState(() => new Set(newProposals.map((p) => p.groupKey)));

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (!proposals || proposals.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, maxWidth: 560, mx: 'auto' }}>
        <CheckIcon sx={{ fontSize: 88, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {localized(UI.allGoodTitle, lang)}
        </Typography>
        <Typography color="text.secondary">{localized(UI.allGoodText, lang)}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center', mb: 1 }}>
        {localized(UI.resultsTitle, lang)}
      </Typography>
      <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
        {localized(UI.proposalsSection, lang)}
      </Typography>

      {BUCKETS.map((bucket) => {
        const items = newProposals.filter((p) => p.bucket === bucket.key);
        if (items.length === 0) return null;
        return (
          <Box key={bucket.key} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: bucket.color }}>
              {bucket.icon}
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {localized(UI[bucket.uiKey], lang)}
              </Typography>
            </Box>
            <Stack spacing={1.5}>
              {items.map((p) => {
                const isChecked = selected.has(p.groupKey);
                return (
                  <Paper
                    key={p.groupKey}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderLeft: `4px solid ${bucket.color}`,
                      backgroundColor: isChecked ? `${bucket.color}08` : 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Checkbox
                        checked={isChecked}
                        onChange={() => toggle(p.groupKey)}
                        sx={{ p: 0.5, color: bucket.color, '&.Mui-checked': { color: bucket.color } }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {t(`thematiques.${p.thematique}`)}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 0.8 }}>
                          {p.item.map((it) => (
                            <Chip key={it} label={t(`riskItems.${it}`)} size="small" variant="outlined" />
                          ))}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {p.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        );
      })}

      {existingProposals.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, color: 'text.secondary' }}>
            <ExistingIcon />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {localized(UI.alreadyExistsTitle, lang)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {localized(UI.alreadyExistsHint, lang)}
          </Typography>
          <Stack spacing={1.5}>
            {existingProposals.map((p) => (
              <Paper
                key={p.groupKey}
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid', borderLeftColor: 'divider',
                  backgroundColor: 'action.hover', opacity: 0.8 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t(`thematiques.${p.thematique}`)}
                  </Typography>
                  <Chip size="small" label={existingLabel(p.existing, lang)} />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 0.8 }}>
                  {p.item.map((it) => (
                    <Chip key={it} label={t(`riskItems.${it}`)} size="small" variant="outlined" />
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {p.description}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddTaskIcon />}
          disabled={creating || selected.size === 0}
          onClick={() => onAccept(Array.from(selected))}
          sx={{
            px: 4, py: 1.5, fontWeight: 700,
            background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
          }}
        >
          {localized(UI.createSelected, lang)} ({selected.size})
        </Button>
      </Box>
    </Box>
  );
}

export default AssessmentResults;
